import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { serverAPI, codeScanAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

interface DashboardData {
  total_targets: number;
  total_scans: number;
  last_scan_at: string | null;
  severity_summary: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  recent_results: RecentResult[];
}

interface RecentResult {
  id: number;
  path: string;
  scanned_at: string;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  scan_duration_seconds: number;
  server_name: string;
  target_label: string | null;
  tech_stack?: { primary_language?: string; framework?: string };
}

interface Target {
  id: number;
  server_id: number;
  server_name: string;
  server_ip: string;
  server_status: string;
  label: string | null;
  path: string;
  schedule: string;
  is_enabled: number;
  last_scanned_at: string | null;
  created_at: string;
}

interface ServerOption {
  id: number;
  name: string;
  public_ip?: string;
  private_ip?: string;
  status: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high:     '#ea580c',
  medium:   '#d97706',
  low:      '#65a30d',
  info:     '#2563eb',
};

const CodeScanDashboardPage: React.FC = () => {
  useDocumentTitle('Source Code Scanner');
  const { currentOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'targets'>('dashboard');

  // Dashboard state
  const [loadingDash, setLoadingDash] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [triggeringServer, setTriggeringServer] = useState<number | null>(null);

  // Targets state
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [targets, setTargets] = useState<Target[]>([]);
  const [servers, setServers] = useState<ServerOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ server_id: '', path: '', label: '', schedule: 'daily' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await codeScanAPI.getDashboard();
      setDashboard(res.data.data);
    } catch {
      // empty state is fine
    } finally {
      setLoadingDash(false);
    }
  }, []);

  const loadTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      // Load targets and servers independently so one failure doesn't block the other
      const [targetsResult, serversResult] = await Promise.allSettled([
        codeScanAPI.listTargets(),
        serverAPI.list(),
      ]);
      if (targetsResult.status === 'fulfilled') {
        setTargets(targetsResult.value.data.data?.targets || []);
      } else {
        toast.error('Failed to load targets');
      }
      if (serversResult.status === 'fulfilled') {
        const svrs = (serversResult.value.data.data?.servers || serversResult.value.data.data || []) as ServerOption[];
        setServers(svrs.filter(s => s.status === 'online'));
      }
    } finally {
      setLoadingTargets(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadTargets();
  }, [loadDashboard, loadTargets, currentOrganization]);

  const handleTriggerScan = async (serverId: number) => {
    setTriggeringServer(serverId);
    try {
      await codeScanAPI.triggerScan(serverId);
      toast.success('Scan command queued — agent will run shortly');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to trigger scan');
    } finally {
      setTriggeringServer(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.server_id || !formData.path) {
      toast.error('Server and path are required');
      return;
    }
    setSaving(true);
    try {
      await codeScanAPI.createTarget({
        server_id: parseInt(formData.server_id),
        path: formData.path,
        label: formData.label || undefined,
        schedule: formData.schedule,
      });
      toast.success('Target added');
      setShowForm(false);
      setFormData({ server_id: '', path: '', label: '', schedule: 'daily' });
      loadTargets();
      loadDashboard();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to add target');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (target: Target) => {
    setTogglingId(target.id);
    try {
      await codeScanAPI.updateTarget(target.id, { is_enabled: target.is_enabled ? 0 : 1 });
      setTargets(prev => prev.map(t => t.id === target.id ? { ...t, is_enabled: t.is_enabled ? 0 : 1 } : t));
    } catch {
      toast.error('Failed to update target');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this scan target?')) return;
    setDeletingId(id);
    try {
      await codeScanAPI.deleteTarget(id);
      setTargets(prev => prev.filter(t => t.id !== id));
      toast.success('Target deleted');
      loadDashboard();
    } catch {
      toast.error('Failed to delete target');
    } finally {
      setDeletingId(null);
    }
  };

  const totalVulns = dashboard
    ? Object.values(dashboard.severity_summary).reduce((a, b) => a + b, 0)
    : 0;

  const onlineServers = servers.filter(s => s.status === 'online');

  return (
    <DashboardLayout>
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <div>
          <h1 className="page-title">Source Code Scanner</h1>
          <p className="page-subtitle">Detect vulnerable dependencies, hardcoded secrets, and EOL runtimes</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e5e7eb', marginBottom: '28px' }}>
        {(['dashboard', 'targets'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '9px 22px',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'none',
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? '#6366f1' : '#6b7280',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {tab === 'dashboard' && <><i className="bi bi-bar-chart-line me-2" />Overview</>}
            {tab === 'targets' && <><i className="bi bi-folder2-open me-2" />Scan Targets {targets.length > 0 && <span className="badge bg-secondary ms-1" style={{ fontSize: '0.7rem' }}>{targets.length}</span>}</>}
          </button>
        ))}
      </div>

      {/* ── Dashboard Tab ─────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <>
          {loadingDash ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <>
              {/* Stat Cards */}
              <div className="row g-3 mb-4">
                <div className="col-sm-6 col-xl-3">
                  <div className="card stat-card h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="stat-icon bg-primary-subtle text-primary">
                          <i className="bi bi-folder2-open fs-4" />
                        </div>
                        <div>
                          <div className="stat-value">{dashboard?.total_targets ?? 0}</div>
                          <div className="stat-label">Scan Targets</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                  <div className="card stat-card h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="stat-icon bg-danger-subtle text-danger">
                          <i className="bi bi-shield-exclamation fs-4" />
                        </div>
                        <div>
                          <div className="stat-value">{dashboard?.severity_summary.critical ?? 0}</div>
                          <div className="stat-label">Critical Vulns</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                  <div className="card stat-card h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="stat-icon bg-warning-subtle text-warning">
                          <i className="bi bi-exclamation-triangle fs-4" />
                        </div>
                        <div>
                          <div className="stat-value">{dashboard?.severity_summary.high ?? 0}</div>
                          <div className="stat-label">High Severity</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                  <div className="card stat-card h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="stat-icon bg-success-subtle text-success">
                          <i className="bi bi-clock-history fs-4" />
                        </div>
                        <div>
                          <div className="stat-value">{dashboard?.total_scans ?? 0}</div>
                          <div className="stat-label">Total Scans</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                {/* Severity Breakdown */}
                {totalVulns > 0 && (
                  <div className="col-xl-4">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Severity Breakdown</h6>
                      </div>
                      <div className="card-body">
                        {Object.entries(dashboard!.severity_summary).map(([sev, count]) => (
                          <div key={sev} className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-capitalize fw-medium" style={{ color: SEVERITY_COLORS[sev] }}>
                                {sev}
                              </span>
                              <span className="fw-bold">{count}</span>
                            </div>
                            <div className="progress" style={{ height: 6 }}>
                              <div
                                className="progress-bar"
                                style={{
                                  width: `${totalVulns > 0 ? (count / totalVulns) * 100 : 0}%`,
                                  backgroundColor: SEVERITY_COLORS[sev],
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        <div className="mt-3 text-muted small">Total: {totalVulns} vulnerabilities</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trigger Scan */}
                {onlineServers.length > 0 && (
                  <div className={totalVulns > 0 ? 'col-xl-8' : 'col-12'}>
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Trigger Manual Scan</h6>
                      </div>
                      <div className="card-body">
                        <p className="text-muted small mb-3">
                          Queue a scan command for an online server. The agent will scan all registered targets.
                        </p>
                        <div className="row g-2">
                          {onlineServers.map(server => (
                            <div key={server.id} className="col-sm-6 col-md-4">
                              <button
                                className="btn btn-outline-primary btn-sm w-100"
                                onClick={() => handleTriggerScan(server.id)}
                                disabled={triggeringServer === server.id}
                              >
                                {triggeringServer === server.id ? (
                                  <span className="spinner-border spinner-border-sm me-2" />
                                ) : (
                                  <i className="bi bi-play-circle me-2" />
                                )}
                                {server.name}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Results */}
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0">Recent Scan Results</h6>
                  <small className="text-muted">
                    Last scanned: {dashboard?.last_scan_at ? new Date(dashboard.last_scan_at).toLocaleString() : 'Never'}
                  </small>
                </div>
                <div className="card-body p-0">
                  {!dashboard?.recent_results?.length ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-code-slash fs-2 d-block mb-2" />
                      <p>No scan results yet.</p>
                      <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('targets')}>
                        Add Scan Target
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Server / Path</th>
                            <th>Stack</th>
                            <th>Critical</th>
                            <th>High</th>
                            <th>Medium</th>
                            <th>Duration</th>
                            <th>Scanned</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard!.recent_results.map(r => (
                            <tr key={r.id}>
                              <td>
                                <div className="fw-medium">{r.server_name}</div>
                                <small className="text-muted">{r.target_label || r.path}</small>
                              </td>
                              <td>
                                <span className="badge bg-secondary-subtle text-secondary">
                                  {r.tech_stack?.primary_language || '—'}
                                </span>
                              </td>
                              <td>
                                {r.critical_count > 0 ? (
                                  <span className="badge text-white" style={{ backgroundColor: SEVERITY_COLORS.critical }}>
                                    {r.critical_count}
                                  </span>
                                ) : <span className="text-muted">0</span>}
                              </td>
                              <td>
                                {r.high_count > 0 ? (
                                  <span className="badge text-white" style={{ backgroundColor: SEVERITY_COLORS.high }}>
                                    {r.high_count}
                                  </span>
                                ) : <span className="text-muted">0</span>}
                              </td>
                              <td>
                                {r.medium_count > 0 ? (
                                  <span className="badge text-white" style={{ backgroundColor: SEVERITY_COLORS.medium }}>
                                    {r.medium_count}
                                  </span>
                                ) : <span className="text-muted">0</span>}
                              </td>
                              <td><span className="text-muted small">{r.scan_duration_seconds}s</span></td>
                              <td><span className="text-muted small">{new Date(r.scanned_at).toLocaleString()}</span></td>
                              <td>
                                <Link to={`/security/code-scan/results/${r.id}`} className="btn btn-outline-primary btn-sm">
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Targets Tab ───────────────────────────────────────────── */}
      {activeTab === 'targets' && (
        <>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h5 className="mb-1 fw-semibold">Scan Targets</h5>
              <p className="text-muted small mb-0">Register application directories for scheduled security scanning</p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              <i className="bi bi-plus-circle me-2" />Add Target
            </button>
          </div>

          {/* Add Target Form */}
          {showForm && (
            <div className="card mb-4 border shadow-sm">
              <div className="card-header bg-light border-0 py-3">
                <h6 className="card-title mb-0 fw-semibold">New Scan Target</h6>
              </div>
              <div className="card-body">
                <form onSubmit={handleCreate}>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-medium">Server <span className="text-danger">*</span></label>
                      {servers.length === 0 ? (
                        <div className="alert alert-warning py-2 small mb-0">
                          <i className="bi bi-exclamation-triangle me-1" />
                          No online servers found. Connect a server first.
                        </div>
                      ) : (
                        <select
                          className="form-select"
                          value={formData.server_id}
                          onChange={e => setFormData(p => ({ ...p, server_id: e.target.value }))}
                          required
                        >
                          <option value="">Select server...</option>
                          {servers.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.name} {s.public_ip ? `(${s.public_ip})` : s.private_ip ? `(${s.private_ip})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-medium">Path <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="/var/www/myapp"
                        value={formData.path}
                        onChange={e => setFormData(p => ({ ...p, path: e.target.value }))}
                        required
                      />
                      <small className="text-muted">Absolute path to app directory</small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-medium">Label</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="My App (optional)"
                        value={formData.label}
                        onChange={e => setFormData(p => ({ ...p, label: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-medium">Scan Schedule <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={formData.schedule}
                        onChange={e => setFormData(p => ({ ...p, schedule: e.target.value }))}
                        required
                      >
                        <option value="daily">Daily — every day at 2am</option>
                        <option value="weekly">Weekly — every Monday at 2am</option>
                        <option value="monthly">Monthly — 1st of each month at 2am</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={saving || servers.length === 0}>
                      {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                      Add Target
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Targets Table */}
          <div className="card">
            <div className="card-body p-0">
              {loadingTargets ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : targets.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-folder2-open fs-2 d-block mb-2" />
                  <p>No scan targets configured.</p>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
                    Add your first target
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">Server</th>
                        <th>Path</th>
                        <th>Label</th>
                        <th>Schedule</th>
                        <th>Last Scanned</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map(t => (
                        <tr key={t.id}>
                          <td className="ps-3">
                            <span className="fw-medium">{t.server_name}</span>
                            <div className="text-muted small">{t.server_ip}</div>
                          </td>
                          <td><code className="small">{t.path}</code></td>
                          <td>{t.label || <span className="text-muted">—</span>}</td>
                          <td>
                            <span className="badge bg-light text-dark border" style={{ fontWeight: 500 }}>
                              <i className="bi bi-calendar me-1" />
                              {t.schedule || 'daily'}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted small">
                              {t.last_scanned_at ? new Date(t.last_scanned_at).toLocaleString() : 'Never'}
                            </span>
                          </td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!t.is_enabled}
                                onChange={() => handleToggle(t)}
                                disabled={togglingId === t.id}
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(t.id)}
                              disabled={deletingId === t.id}
                            >
                              {deletingId === t.id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <i className="bi bi-trash" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default CodeScanDashboardPage;
